"use client";

import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { type User, loginWithHanko, logout as doLogout, getStoredUser } from "@/lib/auth";

interface AuthContextValue {
  user: User | null;
  ready: boolean;
  /** Exchange a Hanko session token for an app session. */
  loginWithToken: (hankoToken: string) => Promise<boolean>;
  /** Re-read the stored app JWT from localStorage and update context state. */
  refreshUser: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setUser(getStoredUser());
    setReady(true);
  }, []);

  const loginWithToken = async (hankoToken: string): Promise<boolean> => {
    const u = await loginWithHanko(hankoToken);
    setUser(u);
    return u !== null;
  };

  const refreshUser = () => setUser(getStoredUser());

  const logout = () => {
    doLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, ready, loginWithToken, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
